import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  PropsWithChildren,
  RefAttributes,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { Icon } from '../icon';

import styles from './carousel.scss';
import { useSlides } from './hooks';

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> &
  PropsWithChildren & {
    step?: number;
    allowScroll?: boolean;
    dataTestId?: string;
    showControlsOnHover?: boolean;
  };

const COMPONENT_ID = 'modulor-carousel';

export const Carousel: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      step = 1,
      allowScroll = false,
      dataTestId = COMPONENT_ID,
      showControlsOnHover = false,
      className,
      ...rest
    },
    ref,
  ) => {
    const carouselWrapperRef = useRef<HTMLDivElement>(null);

    const [touchStart, setTouchStart] = useState(0);
    const [touchTimeStart, setTouchTimeStart] = useState(0);
    const [velocity, setVelocity] = useState(0);

    const { slideBack, slideForward, canSlideBack, canSlideForward, carouselRef, style } =
      useSlides({
        allowScroll,
        velocity,
        step,
        children,
      });

    useImperativeHandle(ref, () => carouselWrapperRef.current);

    const handleStartPoint = useCallback((e) => {
      setTouchStart(e.touches[0].clientX);
      setTouchTimeStart(e.timeStamp);
    }, []);

    const handleEndPoint = useCallback(
      (e) => {
        const deltaDistance = e.changedTouches[0].clientX - touchStart;
        const deltaTime = e.timeStamp - touchTimeStart;

        setVelocity(deltaDistance / deltaTime);
      },
      [touchStart, touchTimeStart],
    );

    return (
      <div
        className={classNames(className, styles.wrapper, {
          [styles['show-controls-on-hover']]: showControlsOnHover,
        })}
        ref={carouselWrapperRef}
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}
        {...rest}>
        {!allowScroll && canSlideBack && (
          <span
            className={classNames(styles.button, styles['button-left'])}
            onClick={slideBack}
            data-id={`${dataTestId}-button-left`}
            data-testid={`${dataTestId}-button-left`}>
            <Icon name="chevron_left" />
          </span>
        )}

        {!allowScroll && canSlideForward && (
          <span
            className={classNames(styles.button, styles['button-right'])}
            onClick={slideForward}
            data-id={`${dataTestId}-button-right`}
            data-testid={`${dataTestId}-button-right`}>
            <Icon name="chevron_right" />
          </span>
        )}

        <div
          className={styles['slides-wrapper']}
          data-id={`${dataTestId}-items-container`}
          data-testid={`${dataTestId}-items-container`}
          ref={carouselRef}
          style={style}
          onTouchStart={handleStartPoint}
          onTouchEnd={handleEndPoint}>
          {children}
        </div>
      </div>
    );
  },
);

Carousel.displayName = 'Carousel';
